// src/pages/HomePage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Icon from '../components/Icon';
import { GameCard, Faq, TopList } from '../components/ui';
import { InstallButton, HomepageButton } from '../components/AppLinks';
import { GAMES, CATEGORIES, site, gameIcon } from '../data';
import { graph, organization, website, gameItemList, faqPage } from '../seo/schema.mjs';

/** Fareyle yatay sürükleyerek kaydırma (dokunmatikte yerel kaydırma zaten var). */
function useDragScroll() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    const onDown = (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
    };
    const onMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (moved) el.scrollLeft = startLeft - dx;
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      el.classList.remove('is-dragging');
    };
    const onClick = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };
    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    el.addEventListener('click', onClick, true);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      el.removeEventListener('click', onClick, true);
    };
  }, []);
  return ref;
}

function useInView(ref, rootMargin = '200px') {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setSeen(true), { rootMargin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, rootMargin, seen]);
  return seen;
}

function HeroTop10() {
  const [data, setData] = useState({ loading: true, list: [] });
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      import('../firebaseHelpers')
        .then(({ getLeaderboard }) => getLeaderboard('totalScore', 10))
        .then((list) => alive && setData({ loading: false, list }))
        .catch(() => alive && setData({ loading: false, list: [] }));
    }, 600);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);
  return <TopList entries={data.list} loading={data.loading} rows={10} />;
}

function LeaderboardsPreview() {
  const ref = useRef(null);
  const visible = useInView(ref);
  const featured = useMemo(() => GAMES.filter((g) => g.popular).slice(0, 3), []);
  const [data, setData] = useState({ loading: true });

  useEffect(() => {
    if (!visible) return;
    let alive = true;
    import('../firebaseHelpers')
      .then(async ({ getLeaderboard, getGameLeaderboard }) => {
        const [general, ...games] = await Promise.all([getLeaderboard('totalScore', 5), ...featured.map((g) => getGameLeaderboard(g.id, 5))]);
        if (alive) setData({ loading: false, general, games });
      })
      .catch(() => alive && setData({ loading: false, general: [], games: [] }));
    return () => {
      alive = false;
    };
  }, [visible, featured]);

  return (
    <div className="lb-grid" ref={ref}>
      <div className="lb-card" style={{ '--c': '#1d1f24' }}>
        <div className="lb-card-head">
          <span className="cat-icon" style={{ '--c': '#a0139f', width: 40, height: 40 }}><Icon name="trophy" size="sm" /></span>
          <h3>Genel sıralama</h3>
          <Link className="see-all" to="/lider-tablosu">Tümü</Link>
        </div>
        <TopList entries={data.general} loading={data.loading} />
      </div>
      {featured.map((g, i) => (
        <div className="lb-card" key={g.id} style={{ '--c': g.accent }}>
          <div className="lb-card-head">
            <img src={gameIcon(g.id)} alt="" width="40" height="40" loading="lazy" />
            <h3>{g.name}</h3>
            <Link className="see-all" style={{ '--c': g.accent }} to={`/lider-tablosu/${g.id}`}>Tümü</Link>
          </div>
          <TopList entries={data.games?.[i]} loading={data.loading} />
        </div>
      ))}
    </div>
  );
}

const ONLINE_GAMES_HOME = [
  { id: 'pisti', players: '2 oyuncu' },
  { id: 'batak', players: '2–4 oyuncu + bot' },
  { id: 'king', players: '2–4 oyuncu + bot' },
  { id: 'ellibir', players: '2–4 oyuncu' },
  { id: 'blum', players: '2 oyuncu' },
];

const FEATURES = [
  { icon: 'users', color: '#3fae2a', title: 'Arkadaşlarla eğlence', text: 'Oda kur, bağlantıyı paylaş; arkadaşlarınla aynı masada keyifli vakit geçir.' },
  { icon: 'bolt', color: '#1fadf0', title: 'Sürekli yenileniyor', text: 'Yeni oyunlar, online masalar ve özellikler düzenli olarak ekleniyor. Her ekranda rahat oynanır.' },
  { icon: 'book', color: '#a0139f', title: 'Doğru kurallar', text: 'Her oyun Türkiye’de yaygın oynanan kurallarla hazırlandı; ayrıntılı rehberleri de var.' },
  { icon: 'trophy', color: '#ff7a1a', title: 'Lider tabloları', text: 'Google ile giriş yap, her galibiyette 10 puan kazan, genel ve oyun sıralamalarında yüksel.' },
];

export default function HomePage() {
  const dragRef = useDragScroll();
  const [cat, setCat] = useState('all');
  const list = cat === 'all' ? GAMES : GAMES.filter((g) => g.category === cat);
  const counts = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c.id, GAMES.filter((g) => g.category === c.id).length])), []);
  const jsonLd = useMemo(() => graph(organization(site), website(site), gameItemList(site, GAMES), faqPage(site, site.faq, '/')), []);

  return (
    <>
      <Seo title={site.title} description={site.description} path="/" jsonLd={jsonLd} />

      <section className="section online-top" id="online" aria-labelledby="online-baslik">
        <div className="container">
          <div className="online-band">
            <div className="online-intro">
              <span className="online-badge">Yeni</span>
              <h2 id="online-baslik">Arkadaşınla online oyna</h2>
              <p>Oda kur, bağlantıyı gönder, aynı masada buluşun. Batak ve King’de boş koltuklara bot oturur.</p>
            </div>
            <div className="online-games">
              {ONLINE_GAMES_HOME.map((o) => {
                const g = GAMES.find((x) => x.id === o.id);
                return (
                  <Link key={o.id} className="online-card" to={`/oyun/${o.id}?mod=online`} style={{ '--c': g.accent }}>
                    <img src={gameIcon(o.id)} alt="" width="56" height="56" loading="lazy" />
                    <span>
                      <strong>{g.name}</strong>
                      <small>{o.players}</small>
                    </span>
                    <span className="online-cta">Oda kur →</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="quick-section" aria-label="Tüm oyunlar, sürükleyerek gez">
        <div className="container">
          <ul className="quick-strip" ref={dragRef}>
            {GAMES.map((g) => (
              <li key={g.id}>
                <Link to={`/oyun/${g.id}`} draggable={false}>
                  <img src={gameIcon(g.id)} alt="" width="72" height="72" draggable={false} />
                  {g.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="hero hero-split">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-main">
          <p className="eyebrow">Online kart oyunları</p>
          <h1>
            Klasik kart oyunları, <span className="hl">tek masada.</span>
          </h1>
          <p className="lead">
            Pişti, Batak, 51, King, Kanasta, Bezik, Pis Yedili ve daha fazlası. Tarayıcında hemen aç, arkadaşlarınla online masa kur. Yeni oyunlar ve özellikler sürekli ekleniyor.
          </p>
          <div className="hero-actions">
            <a className="oy-btn oy-btn-primary oy-btn-lg" href="#oyunlar">
              <Icon name="play" size="sm" /> Hemen oyna
            </a>
            <Link className="oy-btn oy-btn-light oy-btn-lg" to="/rehber">
              Nasıl oynanır?
            </Link>
          </div>
            </div>
            <aside className="hero-lb" aria-labelledby="hero-lb-baslik">
              <div className="lb-card-head">
                <span className="cat-icon" style={{ '--c': '#a0139f', width: 40, height: 40 }}><Icon name="trophy" size="sm" /></span>
                <h2 id="hero-lb-baslik">Genel lider tablosu · İlk 10</h2>
                <Link className="see-all" to="/lider-tablosu">Tümü</Link>
              </div>
              <HeroTop10 />
            </aside>
          </div>
          <div className="app-band app-band-hero app-band-full">
            <div>
              <h2>Oyuntu hep elinin altında</h2>
              <p>Ana ekrana ekle, tek dokunuşla tam ekran aç. Ya da tarayıcının açılış sayfası yap.</p>
            </div>
            <div className="actions">
              <InstallButton className="oy-btn oy-btn-primary oy-btn-sm" />
              <HomepageButton className="oy-btn oy-btn-light oy-btn-sm" />
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="oyunlar" aria-labelledby="oyunlar-baslik">
        <div className="container">
          <div className="section-head">
            <div>
              <h2 id="oyunlar-baslik">Tüm oyunlar</h2>
              <p>Kayıt gerekmez. Giriş yaparsan her galibiyet +10 lider tablosu puanı.</p>
            </div>
          </div>
          <div className="tab-bar" role="tablist" aria-label="Oyun kategorileri">
            <button type="button" role="tab" aria-selected={cat === 'all'} className={`tab${cat === 'all' ? ' active' : ''}`} style={{ '--c': '#1d1f24' }} onClick={() => setCat('all')}>
              <span className="cat-icon" style={{ '--c': '#1d1f24' }}><Icon name="grid" size="sm" /></span>
              Tümü <span className="count-pill">{GAMES.length}</span>
            </button>
            {CATEGORIES.filter((c) => counts[c.id]).map((c) => (
              <button key={c.id} type="button" role="tab" aria-selected={cat === c.id} className={`tab${cat === c.id ? ' active' : ''}`} style={{ '--c': c.color }} onClick={() => setCat(c.id)}>
                <span className="cat-icon" style={{ '--c': c.color }}>{c.emoji}</span>
                {c.name} <span className="count-pill">{counts[c.id]}</span>
              </button>
            ))}
          </div>
          <div className="game-grid">
            {list.map((g) => (
              <GameCard key={g.id} game={g} />
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="neden-baslik">
        <div className="container">
          <div className="section-head">
            <h2 id="neden-baslik">Neden Oyuntu?</h2>
          </div>
          <div className="feature-grid">
            {FEATURES.map((f) => (
              <div className="feature" key={f.title}>
                <span className="cat-icon" style={{ '--c': f.color }}><Icon name={f.icon} /></span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="lider-baslik">
        <div className="container">
          <div className="section-head">
            <div>
              <h2 id="lider-baslik">Lider tabloları</h2>
              <p>Haftanın en iyi oyuncuları burada.</p>
            </div>
            <Link className="see-all" to="/lider-tablosu">Tüm sıralamalar <Icon name="arrow" size="sm" /></Link>
          </div>
          <LeaderboardsPreview />
        </div>
      </section>

      <section className="section" aria-labelledby="sss-baslik">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-5">
              <div className="panel panel-accent h-100">
                <h2 className="h4">Oyuntu hakkında</h2>
                <p className="text-secondary">
                  Oyuntu, Türkiye’de en çok sevilen iskambil oyunlarını tek bir sitede toplar. Pişti’de valeyle masayı süpürün, Batak’ta ihaleyi alıp kozu seçin, 51’de perlerinizi dizin ya da Solitaire ile kafa dinleyin.
                </p>
                <p className="text-secondary mb-0">
                  Oyunlar telefon, tablet ve bilgisayarda doğrudan tarayıcıda çalışır; uygulama indirmeniz gerekmez. Oyuntu’yu ana ekranınıza ekleyerek uygulama gibi de kullanabilirsiniz.
                </p>
              </div>
            </div>
            <div className="col-lg-7">
              <h2 id="sss-baslik" className="h4 mb-3">Sık sorulan sorular</h2>
              <Faq items={site.faq} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
